import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import CreateApi from "../../../shared/Settings/appSettings";
import { useTBContext } from "../../Contexts/useTBStates";
import { LeaderboardDisplay } from "@moment-golf/leaderboard";
import type { SeriesPoints } from "@moment-golf/leaderboard";

interface LeaderboardProps {
    compHeights: any;
    factor: number;
}

export const Leaderboard = ({ compHeights, factor }: LeaderboardProps) => {
    const api = CreateApi();

    const { appTheme, viewEvent, viewEventData, roundValue, viewGroup, groupData } = useTBContext();

    const [importedLeaderboardData, setImportedLeaderboardData] = useState<any[]>([]);
    const [leaderboardLastQuery, setLeaderboardLastQuery] = useState<Date>(
        new Date('2000-01-01')
    );
    const [leaderboardID, setLeaderboardID] = useState<string>('');
    const [resolvedSeriesPoints, setResolvedSeriesPoints] = useState<SeriesPoints | undefined>(undefined);

    // Reset and fetch fresh whenever the viewed event changes
    useEffect(() => {
        setImportedLeaderboardData([]);
        setLeaderboardID('');
        fetchLeaderboardData();

        const interval = setInterval(fetchLeaderboardData, 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewEvent?._id]);

    // Resolve series points whenever the event or viewGroup changes.
    // Priority: viewGroup (if it belongs to this event) → groupData cache → API fetch.
    useEffect(() => {
        const eventGroupIDs: string[] = viewEvent?.eventGroupIDs ?? [];

        if (eventGroupIDs.length === 0) {
            setResolvedSeriesPoints(undefined);
            return;
        }

        // viewGroup is only valid for this event if its _id is in eventGroupIDs
        const viewGroupBelongsToEvent =
            viewGroup?._id && eventGroupIDs.includes(String(viewGroup._id));

        if (viewGroupBelongsToEvent && viewGroup?.seriesPoints) {
            setResolvedSeriesPoints(viewGroup.seriesPoints);
            return;
        }

        // Try the already-loaded groupData first (user has visited the collections section)
        const cachedGroup = groupData.find((g: any) => eventGroupIDs.includes(String(g._id)));
        if (cachedGroup) {
            setResolvedSeriesPoints(cachedGroup.seriesPoints ?? undefined);
            return;
        }

        // Fall back to fetching the first group from the API
        api.get('/tb/GetGroupDetails', { params: { _id: eventGroupIDs[0] } })
            .then((res: any) => {
                setResolvedSeriesPoints(res.data?.seriesPoints ?? undefined);
            })
            .catch(() => setResolvedSeriesPoints(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewEvent?._id, viewGroup?._id]);

    const fetchLeaderboardData = async () => {
        if (!viewEvent?._id) return;

        const response = await api.get('/ls/GetLeaderboardData', {
            params: {
                eventID: viewEvent._id,
                // Use epoch date on first fetch for this event so we get all data
                refDate: viewEvent._id === leaderboardID ? leaderboardLastQuery : new Date('2000-01-01'),
            },
        });

        const data = response.data;
        setLeaderboardLastQuery(new Date());
        setLeaderboardID(viewEvent._id);
        setImportedLeaderboardData(data.eventScores);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                overscrollBehavior: 'none',
                backgroundColor: 'sepia.background',
            }}
            className="live-scoring"
        >
            <LeaderboardDisplay
                leaderboardData={importedLeaderboardData}
                courseData={viewEventData.courseData}
                eventData={viewEvent}
                currentRound={roundValue}
                scoreCode=""
                factor={factor}
                appTheme={appTheme}
                windowSize={{ appBarHeight: 0 }}
                isPWA={false}
                headless={true}
                hideSelector={true}
                hideCourseName={true}
                embedded={true}
                seriesPoints={resolvedSeriesPoints}
            />
        </Box>
    );
};
